import type { PaletteColor } from "@brexhq/metal-foundation";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { padding, margin } from "polished";
import React, { PropsWithChildren } from "react";

import { BodyL, BodyM } from "./Components";
import styles from "./styles";
import type { FontWeights, ListProps } from "./types";
import { filterColorProp } from "./utils";

/**Sometimes type is defined as body small and sometimes BodySmall - there is logic in this file to account for both for now but we should try and figure out why / how there has become a mismatch and patch it appropriately */

export const ListItem: React.FC<
  PropsWithChildren<{
    type?: "BodySmall" | "BodyLarge";
    color?: PaletteColor;
  }>
> = ({ children, type = "BodySmall", color }) => {
  const Components = {
    BodySmall: BodyM,
    "body small": BodyM,
    BodyLarge: BodyL,
    Normal: BodyL,
  };
  const RenderComponent = Components[type];

  const ListItem = styled(RenderComponent)(({ theme }) => ({
    ...padding(0, 0, 0, theme.spacing.p2),
    listStyleType: "disc",
  })).withComponent("li");

  const ListItemText = styled.span({
    color: color ?? "inherit",
  });

  return (
    <ListItem>
      <ListItemText>{children}</ListItemText>
    </ListItem>
  );
};

/**
 * Shared between BulletList and NumberedList
 */
const listStyleMap = {
  Caption: styles.Caption,
  BodySmall: styles.Body2,
  "body small": styles.Body2,
  BodyLarge: styles.Body0,
};

export const BulletList: React.FC<PropsWithChildren<ListProps>> = ({
  children,
  type = "Caption",
  color,
}) => {
  const BulletList = styled.ul(({ theme: { spacing } }) => ({
    ...listStyleMap[type],
    color: color ?? "inherit",
    ...margin(0),
    ...padding(0, 0, 0, spacing.p20),
  }));

  return <BulletList>{children}</BulletList>;
};

export const NumberedList: React.FC<PropsWithChildren<ListProps>> = ({
  children,
  type = "BodyLarge",
  color,
}) => {
  const theme = useTheme();
  const NumberedList = styled.ol(({ theme: { spacing } }) => ({
    ...listStyleMap[type],
    color: color ?? theme.colors.content.tertiary,
    ...margin(0),
    ...padding(0, 0, 0, spacing.p20),
  }));

  return <NumberedList>{children}</NumberedList>;
};

export const Span = styled(
  "span",
  filterColorProp,
)<{ fontWeight?: FontWeights; color?: PaletteColor }>(
  ({ fontWeight, color }) => ({
    fontVariationSettings: fontWeight ? `"wght" ${fontWeight}` : "inherit",
    color: color ? color : "inherit",
  }),
);
